import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const StripeEscrowModal = ({ isOpen, onClose, contract, onFundSuccess }) => {
  const [step, setStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  // Reset step when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setIsProcessing(false);
    }
  }, [isOpen]);

  if (!isOpen || !contract) return null;

  const handleFund = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    
    // Simulate payment processing delay (calling Stripe, locking funds, etc.)
    setTimeout(async () => {
      setIsProcessing(false);
      setStep(2); // Move to success step
      
      // Delay closing modal so they can see the success state
      setTimeout(() => {
        onFundSuccess(contract._id);
      }, 2000);
    }, 2500);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="w-full max-w-md bg-[#0a0a0f] border border-white/10 rounded-2xl shadow-2xl overflow-hidden relative"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center bg-white/5">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-indigo-400">lock</span>
                <h3 className="text-white font-bold tracking-wide">Secure Escrow</h3>
              </div>
              <button onClick={onClose} className="text-white/40 hover:text-white transition-colors" disabled={isProcessing || step === 2}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {step === 1 ? (
                <>
                  <div className="mb-6">
                    <p className="text-white/60 text-sm mb-1">Funding Contract:</p>
                    <h4 className="text-white font-semibold text-lg">{contract.title}</h4>
                    <p className="text-indigo-400 font-mono mt-1">{contract.budget}</p>
                  </div>

                  <form onSubmit={handleFund}>
                    <div className="space-y-4">
                      {/* Fake Stripe Element UI */}
                      <div>
                        <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Card Details</label>
                        <div className="bg-black border border-white/10 rounded-lg p-3 flex items-center gap-3">
                          <span className="material-symbols-outlined text-white/30">credit_card</span>
                          <input 
                            type="text" 
                            placeholder="Card number" 
                            className="bg-transparent border-none focus:ring-0 text-white placeholder-white/30 w-full text-sm outline-none"
                            required
                            defaultValue="4242 4242 4242 4242"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                          <div className="bg-black border border-white/10 rounded-lg p-3">
                            <input type="text" placeholder="MM / YY" className="bg-transparent border-none focus:ring-0 text-white placeholder-white/30 w-full text-sm outline-none" required defaultValue="12 / 28" />
                          </div>
                          <div className="bg-black border border-white/10 rounded-lg p-3">
                            <input type="text" placeholder="CVC" className="bg-transparent border-none focus:ring-0 text-white placeholder-white/30 w-full text-sm outline-none" required defaultValue="123" />
                          </div>
                        </div>
                      </div>

                      {/* Info box */}
                      <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3 flex gap-3 items-start">
                        <span className="material-symbols-outlined text-indigo-400 text-lg">info</span>
                        <p className="text-xs text-indigo-200/70 leading-relaxed">
                          Your funds will be held securely in escrow by Stripe. The money is only released to the talent once you approve the final delivery.
                        </p>
                      </div>
                    </div>

                    <div className="mt-8">
                      <button 
                        type="submit" 
                        disabled={isProcessing}
                        className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 px-4 rounded-xl transition-colors flex justify-center items-center gap-2 disabled:opacity-70 disabled:hover:bg-indigo-600"
                      >
                        {isProcessing ? (
                          <>
                            <span className="material-symbols-outlined animate-spin">refresh</span>
                            Processing...
                          </>
                        ) : (
                          <>
                            <span className="material-symbols-outlined">lock</span>
                            Fund {contract.budget} to Escrow
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mb-4">
                    <span className="material-symbols-outlined text-3xl">check_circle</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Escrow Secured!</h3>
                  <p className="text-white/60 text-sm">
                    The contract has been successfully funded. Talent will be notified immediately.
                  </p>
                </div>
              )}
            </div>
            
            {/* Powered by Stripe */}
            {step === 1 && (
              <div className="bg-black/50 py-3 text-center border-t border-white/5">
                <p className="text-[10px] text-white/30 font-semibold uppercase tracking-widest flex items-center justify-center gap-1">
                  <span className="material-symbols-outlined text-[14px]">lock</span>
                  Secured by Stripe
                </p>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default StripeEscrowModal;
